import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

import { assessmentAdminError } from '../Assessments/api';
import { ScenarioApiError } from './errors';
import {
  BlueprintVersionRow,
  LlmCallDetail,
  LlmCallRow,
  LlmCallsResponse,
  TimelineDocument,
  ValidityResponse,
} from './actionTypes';
import { fetchJob } from './api';
import { isJobLive } from './Jobs';
import { JobKind, JsonObject, ScenarioJob } from './types';

/**
 * The WRITE half of the scenario-builder admin client, and the reads only the
 * action screens need (the family timeline, the LLM call audit, the validity
 * verdict). `api.ts` stays the read surface and is not touched here; both go
 * through the same `apiCaller`, so auth refresh and URL shape stay uniform.
 *
 * One difference from `api.ts`, and it is the reason this file exists: an
 * action needs the STRUCTURED body of a failure, not a flattened sentence.
 * A 409 from the job ledger carries the live job that blocked the submission,
 * and a 409 from activation carries the validity verdict that refused it —
 * both are evidence the screen renders, so `ScenarioActionError` keeps the
 * status and the body alongside the readable message.
 */
export class ScenarioActionError extends ScenarioApiError {
  constructor(message: string, status: number, body: unknown) {
    super(message, status, body);
    this.name = 'ScenarioActionError';
  }
}

type Method = 'GET' | 'POST';

const request = async <Result, Payload extends object = JsonObject>(
  path: string,
  method: Method = 'GET',
  data?: Payload,
): Promise<Result> => {
  const response = await apiCaller<Payload>({
    path,
    method,
    data,
    apiUrl: API_URL,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new ScenarioActionError(
      assessmentAdminError(response.result),
      response.status,
      response.result,
    );
  }
  return response.result as Result;
};

/** The live job a 409 from POST /jobs/ names, when it named one. */
export const conflictingJob = (error: unknown): ScenarioJob | null => {
  if (!(error instanceof ScenarioActionError) || error.status !== 409) {
    return null;
  }
  const body = error.body as { job?: ScenarioJob; live_job?: ScenarioJob };
  return body?.job || body?.live_job || null;
};

/** The 409 body of an activation the gate refused, verbatim. */
export const activationConflict = (error: unknown): JsonObject | null => {
  if (!(error instanceof ScenarioActionError) || error.status !== 409) {
    return null;
  }
  return (error.body || null) as JsonObject | null;
};

/**
 * Submitting is the one job-ledger call the read client does not make: it
 * writes. Reading a job back (`fetchJob`), listing, cancelling and retrying
 * all live in `api.ts`, and this module reuses them rather than owning a
 * second client for the same table.
 */
export const submitJob = (kind: JobKind, payload: JsonObject = {}) =>
  request<ScenarioJob>('/scenario-builder/admin/jobs', 'POST', {
    kind,
    ...payload,
  });

/**
 * The poll cadence a panel watches a job with.
 *
 * It starts fast because most jobs answer fast — a mint apply is fractions of
 * a second, and a refusal is measured in milliseconds — and it backs off
 * because an encode-and-score runs for minutes and hammering the ledger every
 * two seconds for ten of them buys nothing. A constant two-second poll for
 * fifteen minutes is what it did before, and the panel showed no clock, so an
 * admin watching a job nobody would ever pick up saw "PENDING — Waiting for a
 * worker" and no reason to stop believing it.
 */
export const POLL_INTERVAL_START_MS = 1000;
export const POLL_INTERVAL_MAX_MS = 10000;
export const POLL_BACKOFF = 1.5;

/** Past this the panel stops watching; the job keeps running and the Jobs
 *  screen still owns it. */
export const DEFAULT_POLL_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * A job still PENDING after this long was never claimed by a worker. That is
 * not slowness, it is an absence — the celery workers are not running, or not
 * listening to this lane — and it is the one waiting state a panel can
 * diagnose for the admin instead of spinning.
 */
export const UNCLAIMED_AFTER_MS = 60000;

/** A read that fails is retried this many times before the panel gives up on
 *  it: one dropped poll must not end a watch that would have succeeded. */
export const POLL_READ_RETRIES = 3;

export type RunJobOptions = {
  /** Called with every row the poller sees, terminal one included. */
  onUpdate?: (job: ScenarioJob) => void;
  intervalMs?: number;
  timeoutMs?: number;
  /** How long a PENDING job waits before the panel calls it unclaimed. */
  unclaimedAfterMs?: number;
  /** Told when the watch stops for a reason other than the job finishing. */
  onStalled?: (reason: JobWatchStall) => void;
  /** Set true by a closing panel so the poll stops walking. */
  cancelled?: () => boolean;
};

/** Why a panel stopped watching a job that had not finished. */
export type JobWatchStall = {
  kind: 'unclaimed' | 'timeout';
  waitedMs: number;
};

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * Walk a job to a terminal status, reporting every row on the way.
 *
 * Resolves with the last row it saw, whatever that row says: REFUSED and
 * FAILED are outcomes the panels render, never thrown, and so are the two
 * ways a watch ends early — a job no worker claimed, and a job that outlives
 * the panel's patience. Both are reported through `onStalled` so the screen
 * can say which; only a transport or permission failure throws.
 */
export const waitForJob = async (
  created: ScenarioJob,
  options: RunJobOptions = {},
): Promise<ScenarioJob> => {
  const {
    onUpdate,
    intervalMs = POLL_INTERVAL_START_MS,
    timeoutMs = DEFAULT_POLL_TIMEOUT_MS,
    unclaimedAfterMs = UNCLAIMED_AFTER_MS,
    onStalled,
    cancelled,
  } = options;
  let job = created;
  onUpdate?.(job);
  const startedAt = Date.now();
  let wait = intervalMs;
  let consecutiveReadFailures = 0;
  while (isJobLive(job.status)) {
    if (cancelled?.()) return job;
    const waitedMs = Date.now() - startedAt;
    if (waitedMs >= timeoutMs) {
      onStalled?.({ kind: 'timeout', waitedMs });
      return job;
    }
    // PENDING means the ledger holds it and nothing has picked it up.
    // BLOCKED is a different fact — it is waiting for the compute lane, and
    // it has its own copy — so it is never called unclaimed.
    if (
      String(job.status).toUpperCase() === 'PENDING' &&
      !job.started_at &&
      waitedMs >= unclaimedAfterMs
    ) {
      onStalled?.({ kind: 'unclaimed', waitedMs });
      return job;
    }
    await sleep(wait);
    wait = Math.min(Math.round(wait * POLL_BACKOFF), POLL_INTERVAL_MAX_MS);
    if (cancelled?.()) return job;
    try {
      job = await fetchJob(job.id);
      consecutiveReadFailures = 0;
    } catch (readError) {
      // One dropped read is not an outcome. Several in a row is.
      consecutiveReadFailures += 1;
      if (consecutiveReadFailures > POLL_READ_RETRIES) throw readError;
      continue;
    }
    onUpdate?.(job);
  }
  return job;
};

/** Submit a job and wait for its outcome. */
export const runJob = async (
  kind: JobKind,
  payload: JsonObject = {},
  options: RunJobOptions = {},
): Promise<ScenarioJob> => waitForJob(await submitJob(kind, payload), options);

export const fetchTimeline = (params: {
  blueprint?: number | string;
  draft?: number;
}) =>
  request<TimelineDocument, typeof params>(
    '/scenario-builder/admin/timeline',
    'GET',
    params,
  );

/** DRF page envelope, folded into the pagination shape the tables read. */
type DrfPage<Row> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Row[];
};

export type LlmCallFilters = {
  ref_type?: string;
  ref_id?: number;
  call_type?: string;
  status?: string;
  job?: string;
  since?: string;
  until?: string;
  page?: number;
  page_size?: number;
};

export const fetchLlmCalls = async (
  filters: LlmCallFilters = {},
): Promise<LlmCallsResponse> => {
  const result = await request<
    DrfPage<LlmCallRow> | LlmCallsResponse | LlmCallRow[],
    LlmCallFilters
  >('/scenario-builder/admin/llm-calls', 'GET', filters);
  const page = filters.page ?? 1;
  const pageSize = filters.page_size ?? 25;
  if (Array.isArray(result)) {
    return {
      pagination: {
        count: result.length,
        total_pages: 1,
        page: 1,
        page_size: result.length || 1,
        next_page: null,
        previous_page: null,
      },
      results: result,
    };
  }
  if (Array.isArray((result as DrfPage<LlmCallRow>).results)) {
    const envelope = result as DrfPage<LlmCallRow>;
    if (typeof envelope.count === 'number') {
      return {
        pagination: {
          count: envelope.count,
          total_pages: Math.max(1, Math.ceil(envelope.count / pageSize)),
          page,
          page_size: pageSize,
          next_page: envelope.next ? page + 1 : null,
          previous_page: envelope.previous ? page - 1 : null,
        },
        results: envelope.results,
      };
    }
  }
  return result as LlmCallsResponse;
};

export const fetchLlmCall = (id: number) =>
  request<LlmCallDetail>(`/scenario-builder/admin/llm-calls/${id}`);

const scenarioPath = (code: string, suffix = '') =>
  `/assessment/admin/scenarios/${encodeURIComponent(code)}${suffix}`;

/** The stored verdict, and whether it still describes this content. */
export const fetchScenarioValidity = (code: string) =>
  request<ValidityResponse>(scenarioPath(code, '/validity'));

/** Grades this benchmark now and stores the verdict; the gate is read-only. */
export const recomputeScenarioValidity = (code: string) =>
  request<ValidityResponse>(
    scenarioPath(code, '/validity/recompute'),
    'POST',
    {},
  );

export const activateScenario = (
  code: string,
  payload: { override_reason?: string } = {},
) => request<JsonObject>(scenarioPath(code, '/activate'), 'POST', payload);

/**
 * The versions of one blueprint family, from the rollup document.
 *
 * `api.ts` reads the same endpoint but flattens the families into one row per
 * assessment scenario and drops the version list; publishing needs the
 * version id, so the action surface reads it again on demand rather than
 * editing the read client. One request per drawer opening, capped at the
 * endpoint's own page size.
 */
export const fetchBlueprintVersions = async (
  blueprintId: number,
): Promise<BlueprintVersionRow[]> => {
  const result = await request<
    {
      rows?: Array<{
        blueprint?: { id: number };
        versions?: BlueprintVersionRow[];
      }>;
    },
    { page_size: number }
  >('/scenario-builder/admin/benchmarks', 'GET', { page_size: 500 });
  const family = (result?.rows || []).find(
    row => row.blueprint?.id === blueprintId,
  );
  return family?.versions || [];
};
